// src/pages/payroll/TaxTable.jsx
// 근로소득 간이세액표 — 국세청 파일 업로드(시행일 버전 관리, 옛 버전 보존)
//   · 공제대상가족 1명분(C열)만 저장 · 천원→원 변환 · 소득세 조회 테스트
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../api";

const num = (v) => { const n = Number(String(v ?? "0").replace(/[, 원]/g, "")); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => Number(n || 0).toLocaleString();
const isNumeric = (v) => v !== "" && v !== null && v !== undefined && !isNaN(Number(v));
const fmtWon = (n) => `${fmt(n)}원`;

// 파일명에서 시행일 감지: 2026_03_01 / 2026-3-1 / 2026.03.01 → 2026-03-01
const detectEffDate = (filename) => {
  const m = String(filename || "").match(/(20\d{2})[._-](\d{1,2})[._-](\d{1,2})/);
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// 워크북에서 '데이터 시트' 자동 선택 (이상/미만 숫자 행이 가장 많은 시트)
const pickDataSheet = (wb) => {
  let best = null, bestCount = -1;
  for (const name of wb.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, defval: "" });
    const cnt = rows.filter((r) => isNumeric(r[0]) && isNumeric(r[1])).length;
    if (cnt > bestCount) { bestCount = cnt; best = rows; }
  }
  return best || [];
};

// 이상/미만(천원) + 1명 컬럼(인덱스2). '-'·빈칸은 0. from/to는 ×1000(원)으로 변환.
const parseBrackets = (rows) => {
  const out = [];
  for (const r of rows) {
    if (!isNumeric(r[0]) || !isNumeric(r[1])) continue;   // 헤더/수식행 스킵
    const frm = Number(r[0]) * 1000;
    const to = Number(r[1]) * 1000;
    if (to <= frm) continue;
    out.push({ from: frm, to, tax: num(r[2]) });          // num('-')→0
  }
  return out;
};

const readWorkbook = async (file) => {
  const isCsv = /\.csv$/i.test(file.name);
  if (isCsv) {
    const buf = await file.arrayBuffer();
    let text;
    try { text = new TextDecoder("euc-kr").decode(buf); } catch { text = new TextDecoder("utf-8").decode(buf); }
    return XLSX.read(text, { type: "string" });
  }
  return XLSX.read(await file.arrayBuffer(), { type: "array" });
};

export default function TaxTable() {
  const [versions, setVersions] = useState([]);
  const [effDate, setEffDate] = useState("");
  const [srcName, setSrcName] = useState("");
  const [parsed, setParsed] = useState([]);   // 방금 읽은 구간(업로드 전 미리보기)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // 조회 테스트
  const [testSalary, setTestSalary] = useState("");
  const [testResult, setTestResult] = useState(null);

  const loadVersions = async () => {
    try { const r = await api.get("/payroll/tax-table/versions/"); setVersions(Array.isArray(r.data) ? r.data : []); }
    catch { setVersions([]); }
  };
  useEffect(() => { loadVersions(); }, []);

  // ── 파일 읽기(파싱만, 저장 전) ──
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBusy(true); setMsg(`${file.name} 읽는 중...`);
    try {
      const wb = await readWorkbook(file);
      const rows = pickDataSheet(wb);
      const brackets = parseBrackets(rows);
      if (brackets.length === 0) {
        setMsg("이상/미만 구간을 인식하지 못했어요. 국세청 간이세액표 원본인지 확인해 주세요.");
        setBusy(false); return;
      }
      const detected = detectEffDate(file.name);
      setParsed(brackets);
      setSrcName(file.name);
      if (detected) setEffDate(detected);
      setMsg(`${brackets.length}개 구간 인식${detected ? ` · 시행일 ${detected} 감지` : " · 시행일 자동감지 실패 → 직접 선택"}. 확인 후 [저장]을 누르세요.`);
    } catch (err) {
      setMsg("파일 읽기 실패: " + (err.message || err));
    } finally { setBusy(false); }
  };

  // ── 저장(업로드) ── 같은 시행일이면 교체, 다르면 새 버전 추가
  const save = async () => {
    if (parsed.length === 0) { setMsg("먼저 파일을 읽어 주세요."); return; }
    if (!effDate) { setMsg("시행일을 선택해 주세요."); return; }
    if (versions.some((v) => v.effective_date === effDate)) {
      if (!window.confirm(`${effDate} 시행분이 이미 있습니다. 이 구간으로 교체할까요?`)) return;
    }
    setBusy(true);
    try {
      const res = await api.post("/payroll/tax-table/upload/", {
        effective_date: effDate, source_filename: srcName, brackets: parsed,
      });
      setMsg(`${effDate} 시행분 ${res.data?.saved}개 구간 저장 완료.`);
      setParsed([]); setSrcName("");
      await loadVersions();
    } catch (err) {
      setMsg("저장 실패: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const removeVersion = async (v) => {
    if (!window.confirm(`${v.effective_date} 시행분을 삭제할까요?\n(되돌릴 수 없습니다)`)) return;
    setBusy(true);
    try {
      await api.post("/payroll/tax-table/delete/", { version_id: v.id });
      setMsg(`${v.effective_date} 시행분 삭제 완료.`);
      await loadVersions();
    } catch (err) { setMsg("삭제 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const runLookup = async () => {
    const salary = num(testSalary);
    if (!salary) { setTestResult(null); return; }
    try {
      const r = await api.get("/payroll/tax-table/lookup/", { params: { salary } });
      setTestResult(r.data);
    } catch (err) { setMsg("조회 실패: " + (err.response?.data?.detail || err.message)); }
  };

  const S = {
    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
    btn: (c, d) => ({ padding: "6px 12px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),
    btnSm: (c, d) => ({ padding: "4px 10px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),
    th: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "left", whiteSpace: "nowrap" },
    thR: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "right", whiteSpace: "nowrap" },
    td: { padding: "5px 8px", borderBottom: "1px solid #eef2f7" },
    tdR: { padding: "5px 8px", borderBottom: "1px solid #eef2f7", textAlign: "right" },
    input: { padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1" },
    card: { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 16, maxWidth: 760 },
  };

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>근로소득 간이세액표</h2>
      <p style={{ color: "#6b7280", marginBottom: 14 }}>
        국세청 간이세액표 파일을 올리면 <b>공제대상가족 1명분</b>만 저장합니다. 파일을 갱신해 올려도
        <b> 시행일이 다르면 기존 자료는 보존</b>돼요. (급여명세서가 지급일 기준으로 맞는 버전을 사용)
      </p>

      {msg && (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px", borderRadius: 6, marginBottom: 12, color: "#075985" }}>{msg}</div>
      )}

      {/* ── 업로드 ── */}
      <div style={S.card}>
        <b>새 간이세액표 업로드</b>
        <div style={{ ...S.bar, marginTop: 10, marginBottom: parsed.length ? 12 : 0 }}>
          <label style={{ ...S.btn("#0ea5e9", busy), display: "inline-block", opacity: busy ? 0.6 : 1 }}>
            📂 파일 선택 (.xlsx/.csv)
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} disabled={busy} style={{ display: "none" }} />
          </label>
          <label style={{ whiteSpace: "nowrap" }}>시행일</label>
          <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} style={S.input} />
          <button style={S.btn("#16a34a", busy || parsed.length === 0)} disabled={busy || parsed.length === 0} onClick={save}>💾 저장</button>
          {srcName && <span style={{ color: "#64748b" }}>· {srcName}</span>}
        </div>

        {parsed.length > 0 && (
          <>
            <div style={{ color: "#475569", marginBottom: 6 }}>미리보기 — {parsed.length}개 구간 (1명 기준)</div>
            <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr><th style={S.thR}>이상</th><th style={S.thR}>미만</th><th style={S.thR}>소득세</th></tr>
                </thead>
                <tbody>
                  {parsed.map((b, i) => (
                    <tr key={i}>
                      <td style={S.tdR}>{fmtWon(b.from)}</td>
                      <td style={S.tdR}>{fmtWon(b.to)}</td>
                      <td style={S.tdR}>{fmtWon(b.tax)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── 소득세 조회 테스트 ── */}
      <div style={S.card}>
        <b>소득세 조회 테스트</b>
        <p style={{ color: "#94a3b8", fontSize: 12, margin: "4px 0 10px" }}>
          월급여(원, 비과세 없음 = 지급합계 K)를 넣으면 가장 최근 시행분으로 조회합니다.
        </p>

        <div style={S.bar}>
          <input type="text" value={testSalary ? fmt(num(testSalary)) : ""} onChange={(e) => setTestSalary(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runLookup()} placeholder="예: 2,751,450" style={{ ...S.input, width: 160, textAlign: "right" }} />
          <button style={S.btnSm("#2563eb", false)} onClick={runLookup}>조회</button>
          
          {testResult && (
            <span style={{ color: "#334155" }}>
              소득세 <b>{fmtWon(testResult.income_tax)}</b> · 지방소득세 <b>{fmtWon(testResult.local_tax)}</b>
              <span style={{ color: "#94a3b8" }}> (시행일 {testResult.version || "—"})</span>
            </span>
          )}
        </div>
      </div>

      {/* ── 저장된 버전 ── */}
      <div style={S.card}>
        <b>저장된 간이세액표 ({versions.length})</b>
        <div style={{ marginTop: 10, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>시행일</th>
                <th style={S.thR}>구간 수</th>
                <th style={S.th}>원본파일</th>
                <th style={S.th}>등록일시</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 ? (
                <tr><td style={S.td} colSpan={5}>저장된 간이세액표가 없습니다. 파일을 업로드하세요.</td></tr>
              ) : versions.map((v) => (
                <tr key={v.id}>
                  <td style={{ ...S.td, fontWeight: 600 }}>{v.effective_date}</td>
                  <td style={S.tdR}>{v.bracket_count}</td>
                  <td style={{ ...S.td, color: "#64748b", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.source_filename}>{v.source_filename || "—"}</td>
                  <td style={{ ...S.td, color: "#94a3b8" }}>{v.created_at ? String(v.created_at).slice(0, 16).replace("T", " ") : "—"}</td>
                  <td style={S.td}><button style={S.btnSm("#dc2626", busy)} disabled={busy} onClick={() => removeVersion(v)}>🗑 삭제</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}